import { Group, Stack, Box, Text, ActionIcon, Container, Divider, useMantineColorScheme, Image, Drawer } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import { IconMenu2, IconFileText, IconClipboardList, IconChartBar, IconSettings, IconUser, IconLogout, IconNotes, IconBookmark, IconDashboard } from '@tabler/icons-react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import JobListPage from './pages/JobListPage';
import ResumePage from './pages/ResumePage';
import SettingsPage from './pages/SettingsPage';
import NotesPage from './pages/NotesPage';
import NotesEditPage from './pages/NotesEditPage';
import BookmarksPage from './pages/BookmarksPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsPage from './pages/AnalyticsPage'; // Import the new component
import { AuthProvider, useAuth } from './components/common/AuthProvider';
import { RequireAuth } from './components/common/RequireAuth';
import './styles.css';

import logoDark from './assets/logo-dark.svg';
import logoLight from './assets/logo-white.svg';

const mainNavItems = [
  { label: 'Dashboard', icon: IconDashboard, path: '/dashboard' },
  { label: 'Job Board', icon: IconFileText, path: '/jobs' },
  { label: 'Resumes', icon: IconClipboardList, path: '/resumes' },
  { label: 'Notes', icon: IconNotes, path: '/notes' },
  { label: 'Bookmarks', icon: IconBookmark, path: '/bookmarks' },
  { label: 'Analytics', icon: IconChartBar, path: '/analytics' },
];

const bottomNavItems = [
  { label: 'Settings', icon: IconSettings, path: '/settings' },
  { label: 'Profile', icon: IconUser, path: '/profile' }, // Placeholder for now
];

function AuthenticatedApp() {
  const { signOut } = useAuth();
  const { colorScheme } = useMantineColorScheme();
  const [navbarOpened, setNavbarOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const Logo = ({ size = 'sidebar' }: { size?: 'sidebar' | 'mobile' }) => (
    <Stack gap={4} align="center" justify="center" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
      {size === 'sidebar' && (
        <Image
          src={colorScheme === 'dark' ? logoLight : logoDark}
          w={122}
          h={122}
          alt="Note Careers Logo"
        />
      )}
      <Text
        fz={size === 'sidebar' ? 24 : 22}
        fw={900}
        style={{
          fontFamily: "'Merriweather', serif",
          letterSpacing: '-0.05em',
          lineHeight: 1,
          color: colorScheme === 'dark' ? '#ffffff' : '#2C3E3C'
        }}
      >
        note<span style={{ color: '#D16D54' }}>.</span>careers
      </Text>
    </Stack>
  );

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setNavbarOpened(false);
  };

  const renderNavItem = (item: { label: string; icon: any; path: string }) => {
    const Icon = item.icon;
    const isActive = location.pathname.startsWith(item.path);
    return (
      <Box
        key={item.label}
        p="xs"
        px="md"
        onClick={() => handleNavClick(item.path)}
        style={{
          backgroundColor: isActive ? 'rgba(34, 139, 230, 0.2)' : 'transparent',
          color: isActive ? '#228be6' : 'inherit',
          fontWeight: isActive ? 600 : 400,
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Icon size={20} />
        <Text size="sm">{item.label}</Text>
      </Box>
    );
  };

  const Navbar = () => (
    <Box
      p="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Logo Section */}
      <Box mb="xl">
        <Logo size="sidebar" />
      </Box>

      {/* Main Navigation Items */}
      <Stack gap={8}>
        {mainNavItems.map(renderNavItem)}
      </Stack>

      {/* Bottom Items */}
      <Stack gap={0} mt="md">
        <Divider my="md" />
        {bottomNavItems.map(renderNavItem)}
        <Box
          p="xs"
          px="md"
          onClick={() => signOut()}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <IconLogout size={20} />
          <Text size="sm">Logout</Text>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Box style={{ minHeight: '100vh', backgroundColor: 'var(--mantine-color-gray-9)' }}>
      {/* Mobile Header - Only show on mobile */}
      {isMobile && (
        <Group h="60px" px="md" justify="space-between" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Logo size="mobile" />
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={() => setNavbarOpened(true)}
          >
            <IconMenu2 size={24} />
          </ActionIcon>
        </Group>
      )}

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={navbarOpened}
        onClose={() => setNavbarOpened(false)}
        size="75%"
        padding={0}
        withCloseButton={false}
        styles={{
          body: { height: '100%', backgroundColor: 'var(--mantine-color-body)' }
        }}
      >
        <Navbar />
      </Drawer>

      <Container size="xl" px="0" style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Desktop Sidebar - Hidden on mobile */}
        {!isMobile && (
          <Box style={{ width: '200px', borderRight: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <Navbar />
          </Box>
        )}


        {/* Main Content */}
        <Box style={{ flex: 1, width: isMobile ? '100%' : 'auto' }} p="md">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/jobs" element={<JobListPage />} />
            <Route path="/jobs/:jobId" element={<JobListPage />} />
            <Route path="/resumes" element={<ResumePage />} />
            <Route path="/notes" element={<NotesPage
              onEditNote={(noteId) => navigate(`/notes/${noteId}`)}
              onCreateNote={() => navigate('/notes/new')}
            />} />
            <Route path="/notes/new" element={<NotesEditPage noteId="__new__" onBack={() => navigate('/notes')} />} />
            <Route path="/notes/:id" element={<NotesEditPage noteId={location.pathname.split('/').pop() || ''} onBack={() => navigate('/notes')} />} />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
      </Container >
    </Box >
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RequireAuth>
        <AuthenticatedApp />
      </RequireAuth>
    </AuthProvider>
  );
}
